/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { ChangeEvent, ComponentProps, MouseEvent, useState } from 'react';

import { Checkbox } from '../../Elements/Checkbox/index';

import CheckIcon from '@icons/Check.svg';

type Option = { label: string; value: string };

export interface Props {
  /**
   * Type of the dropdown menu
   */
  type?: 'single' | 'multiple';
  /**
   * Options to list in the dropdown menu
   */
  options: Option[];
  /**
   * Selected value or values of the options
   */
  selectedValues?: string | string[];
  /**
   * Unique name of the dropdown menu
   */
  name: string;
  /**
   * Custom callback when an option's onChange event is fired
   */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /**
   * Custom callback when an option's onClick event is fired
   */
  onClick?: (e: MouseEvent<HTMLInputElement>) => void;
  /**
   * ClassName
   */
  className?: string;
  /**
   * Reference to the dropdown menu
   */
  ref?: React.RefObject<HTMLDivElement | null>;
}

export const DropdownMenu = ({
  name,
  options,
  onChange,
  onClick,
  className,
  selectedValues = [],
  type = 'single',
  ref,
}: Props) => {
  const arraySelectedValues = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
  const formattedId = `${name}_dropdown-menu`;

  return (
    <div
      ref={ref}
      id={formattedId}
      className={classNames(
        'h-fit',
        'flex flex-col gap-1',
        'px-1.5 py-1.5',
        'rounded-sm',
        'overflow-y-auto',
        'bg-surface-dark',
        className,
      )}
    >
      {options.map(({ value, label }) => (
        <DropdownMenuItem
          key={label}
          label={label}
          value={value}
          name={name}
          onChange={onChange}
          onClick={onClick}
          selected={arraySelectedValues.includes(value)}
          type={type}
        />
      ))}
    </div>
  );
};

const DropdownMenuItem = ({
  name,
  selected,
  label,
  value,
  onChange,
  onClick,
  type,
}: {
  name: string;
  selected: boolean;
  label: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onClick?: (e: MouseEvent<HTMLInputElement>) => void;
  type: 'single' | 'multiple';
}) => {
  const [isHover, setIsHover] = useState(false);
  const [isFocus, setIsFocus] = useState(false);

  const formattedId = `${name}_${label.replace(/\s+/g, '_')}`;

  const onMouseEnter = () => {
    setIsHover(true);
  };
  const onMouseLeave = () => {
    setIsHover(false);
  };
  const onFocus = () => {
    setIsFocus(true);
  };
  const onBlur = () => {
    setIsFocus(false);
  };

  const hasState = isHover || selected || isFocus;

  const commonInputProps: ComponentProps<'input'> = {
    name: name,
    value: value,
    checked: selected,
    onChange: onChange,
    onClick: onClick,
    className: 'absolute inset-0 opacity-0',
  };

  return (
    <label
      htmlFor={formattedId}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      className={classNames(
        'relative',
        'flex items-center justify-start gap-2',
        'py-2.5 pr-2.5 pl-2',
        'font-semibold',
        'rounded-sm',
        'transition-colors duration-200',
        '[&:has(:focus-visible)]:border [&:has(:focus-visible)]:border-neutral-text-light',
        {
          'bg-surface-medium': isHover || selected,
          'bg-surface-dark': !(isHover || selected),
          'text-neutral-text-light': hasState,
          'text-neutral-text-medium': !hasState,
        },
      )}
    >
      {type === 'single' && (
        <>
          <input type='radio' id={formattedId} {...commonInputProps} />
          <span className='inline-flex'>
            <span className='mr-2 size-6'>
              <AnimatePresence>
                {selected && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.3,
                    }}
                  >
                    <CheckIcon className='size-full text-neutral-text-light' />
                  </motion.span>
                )}
              </AnimatePresence>
            </span>

            {label}
          </span>
        </>
      )}

      {type === 'multiple' && (
        <>
          <input type='checkbox' id={formattedId} {...commonInputProps} />
          <Checkbox checked={selected} hover={isHover} focus={isFocus} />
          <span className='flex grow justify-between'>{label}</span>
        </>
      )}
    </label>
  );
};
